import { useState } from 'react';
import OrdersTable from './OrdersTable';
import OrderDetailModal from './OrderDetailModal';
import ExportConfigModal from './ExportConfigModal';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { buildCsv, DEFAULT_COLUMNS_FULL } from '../utils/exportColumns';

function QuickViewStats({
    stats,
    theme,
    showOpenTabs,
    setShowOpenTabs,
    showClosedTabs,
    setShowClosedTabs,
    showTotalTabs,
    setShowTotalTabs,
    showCashTabs,
    setShowCashTabs,
    showCreditTabs,
    setShowCreditTabs,
    showOtherTabs,
    setShowOtherTabs,
    showAverageTimesTabs,
    setShowAverageTimesTabs,
    openOrders,
    closedOrders,
    totalOrders,
    cashOrders,
    creditOrders,
    otherOrders,
    timePeriod,
    setTimePeriod,
    selectedDate,
    setSelectedDate,
    selectedHour,
    setSelectedHour,
    averageTimesByArea,
    onDataRefresh, // Add callback to refresh data when order is reopened
    onCreateInvoice, // Add callback for creating invoices
    user // Add user prop for permissions
}) {
    const { t, language, translateInLanguage } = useLanguage();
    const [exporting, setExporting] = useState(false);
    const [showExportConfig, setShowExportConfig] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showOrderDetail, setShowOrderDetail] = useState(false);

    // Helper function to close all tabs
    const closeAllTabs = () => {
        setShowOpenTabs(false);
        setShowClosedTabs(false);
        setShowTotalTabs(false);
        setShowCashTabs(false);
        setShowCreditTabs(false);
        setShowOtherTabs(false);
        setShowAverageTimesTabs(false);
    };

    // Helper function to toggle a specific tab (closes others)
    const toggleTab = (tabSetter, currentState) => {
        if (currentState) {
            // If this tab is already open, just close it
            tabSetter(false);
        } else {
            // Close all tabs first, then open this one
            closeAllTabs();
            tabSetter(true);
        }
    };

    const handleExportStatistics = () => {
        setShowExportConfig(true);
    };

    const runExport = async ({ language: exportLang, selectedColumns }) => {
        setExporting(true);
        try {
            let url = timePeriod === 'all'
                ? `${BACKEND_URL}/api/admin/export-statistics`
                : `${BACKEND_URL}/api/admin/export-statistics?period=${timePeriod}`;

            if (timePeriod !== 'all' && selectedDate) {
                url += `&date=${selectedDate}`;
            }
            if (timePeriod === 'hour' && selectedHour !== undefined) {
                url += `&hour=${selectedHour}`;
            }

            const response = await fetch(url);
            if (!response.ok) throw new Error(t('failedToFetchExportData'));
            const data = await response.json();

            const csvContent = buildCsv(data, selectedColumns, translateInLanguage, exportLang);

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url_obj = URL.createObjectURL(blob);
            link.setAttribute('href', url_obj);

            const exportDate = new Date().toISOString().split('T')[0];
            const dates = data.map(r => r.order_created).filter(Boolean).map(d => new Date(d));
            const fromDate = dates.length > 0 ? new Date(Math.min(...dates)).toISOString().split('T')[0] : exportDate;
            const toDate = dates.length > 0 ? new Date(Math.max(...dates)).toISOString().split('T')[0] : exportDate;
            const periodText = timePeriod === 'all' ? 'all_time' : timePeriod;
            const filename = `order_statistics_${periodText}_${fromDate}_to_${toDate}_exported_${exportDate}.csv`;
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            alert(t('successfullyExportedOrderItems').replace('{count}', data.length).replace('{filename}', filename));
            setShowExportConfig(false);
        } catch (err) {
            alert(t('errorExportingStatistics') + ': ' + err.message);
        } finally {
            setExporting(false);
        }
    };

    const handleOrderClick = (order) => {
        setSelectedOrder(order);
        setShowOrderDetail(true);
    };

    const handleCloseOrderDetail = () => {
        setShowOrderDetail(false);
        setSelectedOrder(null);
    };

    const handleOrderReopened = () => {
        if (onDataRefresh) {
            onDataRefresh();
        }
    };

    const navigateDate = (direction) => {
        const [year, month, day] = selectedDate.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        date.setDate(date.getDate() + direction);
        const newDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        setSelectedDate(newDate);
    };

    const navigateWeek = (direction) => {
        const [year, month, day] = selectedDate.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        date.setDate(date.getDate() + (direction * 7));
        const newDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        setSelectedDate(newDate);
    };

    const navigateMonth = (direction) => {
        const [year, month, day] = selectedDate.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        date.setMonth(date.getMonth() + direction);
        const newDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        setSelectedDate(newDate);
    };

    const navigateYear = (direction) => {
        const [year, month, day] = selectedDate.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        date.setFullYear(date.getFullYear() + direction);
        const newDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        setSelectedDate(newDate);
    };

    // Get selected month in YYYY-MM format for month picker
    const getSelectedMonth = () => {
        if (!selectedDate) return '';
        const [year, month] = selectedDate.split('-');
        return `${year}-${month}`;
    };

    const handleMonthChange = (monthValue) => {
        const [year, month] = monthValue.split('-');
        const newDate = `${year}-${month}-01`;
        setSelectedDate(newDate);
    };

    // Get selected year
    const getSelectedYear = () => {
        if (!selectedDate) return '';
        const [year] = selectedDate.split('-');
        return year;
    };

    const handleYearChange = (yearValue) => {
        const newDate = `${yearValue}-01-01`;
        setSelectedDate(newDate);
    };

    // Get week range display (Mon-Sun)
    const getWeekRange = () => {
        if (!selectedDate) return '';
        const [year, month, day] = selectedDate.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        // Get Monday of the week
        const dayOfWeek = date.getDay();
        const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // adjust when day is Sunday
        const monday = new Date(year, month - 1, diff);

        // Get Sunday of the week
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);

        const formatDate = (d) => `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
        return `${formatDate(monday)} - ${formatDate(sunday)}`;
    };

    // Navigate hour
    const navigateHour = (direction) => {
        const newHour = selectedHour + direction;

        if (newHour > 23) {
            // Going past 11 PM, move to next day at midnight
            setSelectedHour(0);
            navigateDate(1);
        } else if (newHour < 0) {
            // Going before 12 AM, move to previous day at 11 PM
            setSelectedHour(23);
            navigateDate(-1);
        } else {
            setSelectedHour(newHour);
        }
    };

    return (
        <>
            {/* Time Period Selector */}
            <div style={{
                marginBottom: '1.5rem',
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                        onClick={() => setTimePeriod('hour')}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: timePeriod === 'hour' ? theme.colors.primary : theme.colors.surface,
                            color: timePeriod === 'hour' ? theme.colors.surface : theme.colors.text,
                            border: `1px solid ${theme.colors.border}`,
                            borderRadius: theme.borderRadius || 4,
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: timePeriod === 'hour' ? 'bold' : 'normal'
                        }}
                    >
                        {t('byHour')}
                    </button>
                    <button
                        onClick={() => setTimePeriod('today')}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: timePeriod === 'today' ? theme.colors.primary : theme.colors.surface,
                            color: timePeriod === 'today' ? theme.colors.surface : theme.colors.text,
                            border: `1px solid ${theme.colors.border}`,
                            borderRadius: theme.borderRadius || 4,
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: timePeriod === 'today' ? 'bold' : 'normal'
                        }}
                    >
                        {t('byDay')}
                    </button>
                    <button
                        onClick={() => setTimePeriod('week')}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: timePeriod === 'week' ? theme.colors.primary : theme.colors.surface,
                            color: timePeriod === 'week' ? theme.colors.surface : theme.colors.text,
                            border: `1px solid ${theme.colors.border}`,
                            borderRadius: theme.borderRadius || 4,
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: timePeriod === 'week' ? 'bold' : 'normal'
                        }}
                    >
                        {t('byWeek')}
                    </button>
                    <button
                        onClick={() => setTimePeriod('month')}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: timePeriod === 'month' ? theme.colors.primary : theme.colors.surface,
                            color: timePeriod === 'month' ? theme.colors.surface : theme.colors.text,
                            border: `1px solid ${theme.colors.border}`,
                            borderRadius: theme.borderRadius || 4,
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: timePeriod === 'month' ? 'bold' : 'normal'
                        }}
                    >
                        {t('byMonth')}
                    </button>
                    <button
                        onClick={() => setTimePeriod('year')}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: timePeriod === 'year' ? theme.colors.primary : theme.colors.surface,
                            color: timePeriod === 'year' ? theme.colors.surface : theme.colors.text,
                            border: `1px solid ${theme.colors.border}`,
                            borderRadius: theme.borderRadius || 4,
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: timePeriod === 'year' ? 'bold' : 'normal'
                        }}
                    >
                        {t('byYear')}
                    </button>
                    <button
                        onClick={() => setTimePeriod('all')}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: timePeriod === 'all' ? theme.colors.primary : theme.colors.surface,
                            color: timePeriod === 'all' ? theme.colors.surface : theme.colors.text,
                            border: `1px solid ${theme.colors.border}`,
                            borderRadius: theme.borderRadius || 4,
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: timePeriod === 'all' ? 'bold' : 'normal'
                        }}
                    >
                        {t('allTime')}
                    </button>
                </div>

                {timePeriod === 'hour' && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <button
                            onClick={() => navigateHour(-1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            ←
                        </button>
                        <select
                            value={selectedHour}
                            onChange={(e) => setSelectedHour(Number(e.target.value))}
                            style={{
                                padding: '0.5rem',
                                borderRadius: theme.borderRadius || 4,
                                border: `1px solid ${theme.colors.border}`,
                                fontSize: '0.9rem',
                                color: theme.colors.text,
                                backgroundColor: theme.colors.surface,
                                width: '100px'
                            }}
                        >
                            {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>
                                    {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => navigateHour(1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            →
                        </button>
                        <button
                            onClick={() => navigateDate(-1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            ←
                        </button>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            style={{
                                padding: '0.5rem',
                                borderRadius: theme.borderRadius || 4,
                                border: `1px solid ${theme.colors.border}`,
                                fontSize: '0.9rem',
                                color: theme.colors.text,
                                backgroundColor: theme.colors.surface
                            }}
                        />
                        <button
                            onClick={() => navigateDate(1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            →
                        </button>
                    </div>
                )}

                {timePeriod === 'today' && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <button
                            onClick={() => navigateDate(-1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            ←
                        </button>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            style={{
                                padding: '0.5rem',
                                borderRadius: theme.borderRadius || 4,
                                border: `1px solid ${theme.colors.border}`,
                                fontSize: '0.9rem',
                                color: theme.colors.text,
                                backgroundColor: theme.colors.surface
                            }}
                        />
                        <button
                            onClick={() => navigateDate(1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            →
                        </button>
                    </div>
                )}

                {timePeriod === 'week' && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <button
                            onClick={() => navigateWeek(-1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            ←
                        </button>
                        <div
                            style={{
                                padding: '0.5rem',
                                borderRadius: theme.borderRadius || 4,
                                border: `1px solid ${theme.colors.border}`,
                                fontSize: '0.9rem',
                                color: theme.colors.text,
                                backgroundColor: theme.colors.surface,
                                minWidth: '180px',
                                textAlign: 'center'
                            }}
                        >
                            {getWeekRange()}
                        </div>
                        <button
                            onClick={() => navigateWeek(1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            →
                        </button>
                    </div>
                )}

                {timePeriod === 'month' && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <button
                            onClick={() => navigateMonth(-1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            ←
                        </button>
                        <input
                            type="month"
                            value={getSelectedMonth()}
                            onChange={(e) => handleMonthChange(e.target.value)}
                            style={{
                                padding: '0.5rem',
                                borderRadius: theme.borderRadius || 4,
                                border: `1px solid ${theme.colors.border}`,
                                fontSize: '0.9rem',
                                color: theme.colors.text,
                                backgroundColor: theme.colors.surface
                            }}
                        />
                        <button
                            onClick={() => navigateMonth(1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            →
                        </button>
                    </div>
                )}

                {timePeriod === 'year' && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <button
                            onClick={() => navigateYear(-1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            ←
                        </button>
                        <input
                            type="number"
                            min="2020"
                            max="2099"
                            value={getSelectedYear()}
                            onChange={(e) => handleYearChange(e.target.value)}
                            onWheel={(e) => e.target.blur()}
                            style={{
                                padding: '0.5rem',
                                borderRadius: theme.borderRadius || 4,
                                border: `1px solid ${theme.colors.border}`,
                                fontSize: '0.9rem',
                                color: theme.colors.text,
                                backgroundColor: theme.colors.surface,
                                width: '100px',
                                textAlign: 'center'
                            }}
                        />
                        <button
                            onClick={() => navigateYear(1)}
                            style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            →
                        </button>
                    </div>
                )}

                <button
                    onClick={handleExportStatistics}
                    disabled={exporting}
                    style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: theme.colors.secondary || '#666',
                        color: theme.colors.surface,
                        border: 'none',
                        borderRadius: theme.borderRadius || 4,
                        cursor: exporting ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        opacity: exporting ? 0.5 : 1
                    }}
                >
                    📤 {exporting ? t('exporting') + '...' : t('export')}
                </button>
            </div>

            {/* Statistics Grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1.5rem',
                marginBottom: '1.5rem'
            }}>
                <button
                    onClick={() => toggleTab(setShowOpenTabs, showOpenTabs)}
                    style={{
                        background: theme.colors.surface,
                        padding: '1.5rem',
                        borderRadius: theme.borderRadius || 8,
                        boxShadow: theme.boxShadow,
                        textAlign: 'center',
                        border: `2px solid ${showOpenTabs ? theme.colors.primary : 'transparent'}`,
                        cursor: 'pointer',
                        width: '100%'
                    }}
                >
                    <h3 style={{ color: theme.colors.primary, margin: '0 0 0.5rem 0' }}>
                        {t('openChecks')} ({stats.openOrders})
                    </h3>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: theme.colors.warning || '#ff9800' }}>
                        ${stats.openRevenue.toFixed(2)}
                    </div>
                </button>

                <button
                    onClick={() => toggleTab(setShowClosedTabs, showClosedTabs)}
                    style={{
                        background: theme.colors.surface,
                        padding: '1.5rem',
                        borderRadius: theme.borderRadius || 8,
                        boxShadow: theme.boxShadow,
                        textAlign: 'center',
                        border: `2px solid ${showClosedTabs ? theme.colors.primary : 'transparent'}`,
                        cursor: 'pointer',
                        width: '100%'
                    }}
                >
                    <h3 style={{ color: theme.colors.primary, margin: '0 0 0.5rem 0' }}>
                        {t('closedChecks')} ({stats.closedOrders})
                    </h3>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: theme.colors.success || '#4caf50' }}>
                        ${stats.closedRevenue.toFixed(2)}
                    </div>
                </button>

                <button
                    onClick={() => toggleTab(setShowTotalTabs, showTotalTabs)}
                    style={{
                        background: theme.colors.surface,
                        padding: '1.5rem',
                        borderRadius: theme.borderRadius || 8,
                        boxShadow: theme.boxShadow,
                        textAlign: 'center',
                        border: `2px solid ${showTotalTabs ? theme.colors.primary : 'transparent'}`,
                        cursor: 'pointer',
                        width: '100%'
                    }}
                >
                    <h3 style={{ color: theme.colors.primary, margin: '0 0 0.5rem 0' }}>
                        {t('totalRevenue')} ({stats.totalOrders})
                    </h3>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: theme.colors.primary }}>
                        ${stats.totalRevenue.toFixed(2)}
                    </div>
                </button>

                <div
                    style={{
                        background: theme.colors.surface,
                        padding: '1.5rem',
                        borderRadius: theme.borderRadius || 8,
                        boxShadow: theme.boxShadow,
                        textAlign: 'center',
                        width: '100%'
                    }}
                >
                    <h3 style={{ color: theme.colors.primary, margin: '0 0 0.5rem 0' }}>
                        ⭐ {t('avgOrderTotal')}
                    </h3>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: theme.colors.warning || '#ff9800' }}>
                        ${stats.avgOrderTotal?.toFixed(2) || '0.00'}
                    </div>
                </div>

                <button
                    onClick={() => toggleTab(setShowCashTabs, showCashTabs)}
                    style={{
                        background: theme.colors.surface,
                        padding: '1.5rem',
                        borderRadius: theme.borderRadius || 8,
                        boxShadow: theme.boxShadow,
                        textAlign: 'center',
                        border: `2px solid ${showCashTabs ? theme.colors.primary : 'transparent'}`,
                        cursor: 'pointer',
                        width: '100%'
                    }}
                >
                    <h3 style={{ color: theme.colors.primary, margin: '0 0 0.5rem 0' }}>
                        💵 {t('cash')} ({cashOrders.length})
                    </h3>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: theme.colors.success || '#4caf50' }}>
                        ${stats.cashRevenue.toFixed(2)}
                    </div>
                </button>

                <button
                    onClick={() => toggleTab(setShowCreditTabs, showCreditTabs)}
                    style={{
                        background: theme.colors.surface,
                        padding: '1.5rem',
                        borderRadius: theme.borderRadius || 8,
                        boxShadow: theme.boxShadow,
                        textAlign: 'center',
                        border: `2px solid ${showCreditTabs ? theme.colors.primary : 'transparent'}`,
                        cursor: 'pointer',
                        width: '100%'
                    }}
                >
                    <h3 style={{ color: theme.colors.primary, margin: '0 0 0.5rem 0' }}>
                        💳 {t('creditDebit')} ({creditOrders.length})
                    </h3>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: theme.colors.secondary || '#2196f3' }}>
                        ${stats.creditRevenue.toFixed(2)}
                    </div>
                </button>

                <button
                    onClick={() => toggleTab(setShowOtherTabs, showOtherTabs)}
                    style={{
                        background: theme.colors.surface,
                        padding: '1.5rem',
                        borderRadius: theme.borderRadius || 8,
                        boxShadow: theme.boxShadow,
                        textAlign: 'center',
                        border: `2px solid ${showOtherTabs ? theme.colors.primary : 'transparent'}`,
                        cursor: 'pointer',
                        width: '100%'
                    }}
                >
                    <h3 style={{ color: theme.colors.primary, margin: '0 0 0.5rem 0' }}>
                        🏦 {t('otherPayment')} ({otherOrders.length})
                    </h3>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: theme.colors.textSecondary || '#9c27b0' }}>
                        ${stats.otherRevenue.toFixed(2)}
                    </div>
                </button>

                {/* Average Finish Time Card */}
                {averageTimesByArea && averageTimesByArea.length > 0 && (() => {
                    // Calculate overall average from all areas
                    let totalFinishedItems = 0;
                    let weightedTotalTime = 0;

                    averageTimesByArea.forEach(area => {
                        const finishedItems = area.finished_items || 0;
                        const avgTime = area.avg_total_time_minutes || 0;
                        totalFinishedItems += finishedItems;
                        weightedTotalTime += (finishedItems * avgTime);
                    });

                    const overallAverageTime = totalFinishedItems > 0 ? weightedTotalTime / totalFinishedItems : 0;

                    return (
                        <button
                            onClick={() => toggleTab(setShowAverageTimesTabs, showAverageTimesTabs)}
                            style={{
                                background: theme.colors.surface,
                                padding: '1.5rem',
                                borderRadius: theme.borderRadius || 8,
                                boxShadow: theme.boxShadow,
                                textAlign: 'center',
                                border: `2px solid ${showAverageTimesTabs ? theme.colors.primary : 'transparent'}`,
                                cursor: 'pointer',
                                width: '100%'
                            }}
                        >
                            <h3 style={{ color: theme.colors.primary, margin: '0 0 0.5rem 0' }}>
                                ⏱️ {t('avgFinishTime')}
                            </h3>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#795548' }}>
                                {Math.round(overallAverageTime)}m
                            </div>
                        </button>
                    );
                })()}

            </div>

            {/* Average Finish Times by Area - Detailed View */}
            {showAverageTimesTabs && averageTimesByArea && averageTimesByArea.length > 0 && (
                <div style={{
                    background: theme.colors.surface,
                    padding: '1.5rem',
                    borderRadius: theme.borderRadius || 8,
                    boxShadow: theme.boxShadow,
                    marginBottom: '1.5rem'
                }}>
                    <h2 style={{ color: theme.colors.primary, margin: '0 0 1rem 0' }}>
                        ⏱️ Average Finish Times by Area ({averageTimesByArea.length} areas)
                    </h2>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '1rem'
                        }}>
                            {averageTimesByArea.map(areaData => (
                                <div key={areaData.area} style={{
                                    background: theme.colors.background,
                                    padding: '1rem',
                                    borderRadius: theme.borderRadius || 6,
                                    border: `1px solid ${theme.colors.primary}20`,
                                    textAlign: 'center'
                                }}>
                                    <h3 style={{
                                        color: theme.colors.primary,
                                        margin: '0 0 0.5rem 0',
                                        textTransform: 'capitalize'
                                    }}>
                                        {areaData.area || 'Unknown'}
                                    </h3>
                                    <div style={{ fontSize: '0.9rem', color: theme.colors.text }}>
                                        <div style={{ marginBottom: '0.25rem' }}>
                                            <strong>Finished Items:</strong> {areaData.finished_items}
                                        </div>
                                        <div style={{ marginBottom: '0.25rem' }}>
                                            <strong>Avg Total:</strong> {areaData.avg_total_time_minutes ? `${Math.round(areaData.avg_total_time_minutes)}m` : 'N/A'}
                                        </div>
                                        <div style={{ marginBottom: '0.25rem' }}>
                                            <strong>Avg Wait:</strong> {areaData.avg_wait_time_minutes ? `${Math.round(areaData.avg_wait_time_minutes)}m` : 'N/A'}
                                        </div>
                                        <div>
                                            <strong>Avg Prep:</strong> {areaData.avg_prep_time_minutes ? `${Math.round(areaData.avg_prep_time_minutes)}m` : 'N/A'}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Open Orders Section */}
            {showOpenTabs && (
                <div style={{
                    background: theme.colors.surface,
                    padding: '1.5rem',
                    borderRadius: theme.borderRadius || 8,
                    boxShadow: theme.boxShadow,
                    marginBottom: '1.5rem'
                }}>
                    <h2 style={{ color: theme.colors.primary, margin: '0 0 1rem 0' }}>
                        {t('openOrders')} ({openOrders.length})
                    </h2>
                    <OrdersTable
                        orders={openOrders}
                        orderType="open"
                        theme={theme}
                        t={t}
                        onOrderClick={handleOrderClick}
                    />
                </div>
            )}

            {/* Closed Orders Section */}
            {showClosedTabs && (
                <div style={{
                    background: theme.colors.surface,
                    padding: '1.5rem',
                    borderRadius: theme.borderRadius || 8,
                    boxShadow: theme.boxShadow,
                    marginBottom: '1.5rem'
                }}>
                    <h2 style={{ color: theme.colors.primary, margin: '0 0 1rem 0' }}>
                        {t('closedOrders')} ({closedOrders.length})
                    </h2>
                    <OrdersTable
                        orders={closedOrders}
                        orderType="closed"
                        theme={theme}
                        t={t}
                        onOrderClick={handleOrderClick}
                    />
                </div>
            )}

            {/* Cash Orders Section */}
            {showCashTabs && (
                <div style={{
                    background: theme.colors.surface,
                    padding: '1.5rem',
                    borderRadius: theme.borderRadius || 8,
                    boxShadow: theme.boxShadow,
                    marginBottom: '1.5rem'
                }}>
                    <h2 style={{ color: theme.colors.primary, margin: '0 0 1rem 0' }}>
                        💵 {t('cash')} {t('orders')} ({cashOrders.length})
                    </h2>
                    <OrdersTable
                        orders={cashOrders}
                        orderType="closed"
                        theme={theme}
                        t={t}
                        onOrderClick={handleOrderClick}
                    />
                </div>
            )}

            {/* Credit Orders Section */}
            {showCreditTabs && (
                <div style={{
                    background: theme.colors.surface,
                    padding: '1.5rem',
                    borderRadius: theme.borderRadius || 8,
                    boxShadow: theme.boxShadow,
                    marginBottom: '1.5rem'
                }}>
                    <h2 style={{ color: theme.colors.primary, margin: '0 0 1rem 0' }}>
                        💳 {t('creditDebit')} {t('orders')} ({creditOrders.length})
                    </h2>
                    <OrdersTable
                        orders={creditOrders}
                        orderType="closed"
                        theme={theme}
                        t={t}
                        onOrderClick={handleOrderClick}
                    />
                </div>
            )}

            {/* Other Payment Orders Section */}
            {showOtherTabs && (
                <div style={{
                    background: theme.colors.surface,
                    padding: '1.5rem',
                    borderRadius: theme.borderRadius || 8,
                    boxShadow: theme.boxShadow,
                    marginBottom: '1.5rem'
                }}>
                    <h2 style={{ color: theme.colors.primary, margin: '0 0 1rem 0' }}>
                        🏦 {t('otherPayment')} {t('orders')} ({otherOrders.length})
                    </h2>
                    <OrdersTable
                        orders={otherOrders}
                        orderType="closed"
                        theme={theme}
                        t={t}
                        onOrderClick={handleOrderClick}
                    />
                </div>
            )}

            {/* Total Orders Section */}
            {showTotalTabs && (
                <div style={{
                    background: theme.colors.surface,
                    padding: '1.5rem',
                    borderRadius: theme.borderRadius || 8,
                    boxShadow: theme.boxShadow,
                    marginBottom: '1.5rem'
                }}>
                    <h2 style={{ color: theme.colors.primary, margin: '0 0 1rem 0' }}>
                        📊 {t('allOrders')} ({totalOrders.length})
                    </h2>
                    <OrdersTable
                        orders={totalOrders}
                        orderType="closed"
                        theme={theme}
                        t={t}
                        onOrderClick={handleOrderClick}
                    />
                </div>
            )}

            {/* Order Detail Modal */}
            {showOrderDetail && selectedOrder && (
                <OrderDetailModal
                    order={selectedOrder}
                    theme={theme}
                    onClose={handleCloseOrderDetail}
                    onOrderReopened={handleOrderReopened}
                    onCreateInvoice={onCreateInvoice}
                    onOrderDeleted={handleOrderReopened}
                    user={user}
                />
            )}

            <ExportConfigModal
                isOpen={showExportConfig}
                onClose={() => setShowExportConfig(false)}
                onConfirm={runExport}
                theme={theme}
                callerKey="quickViewStats"
                defaultLanguage={language}
                defaultSelectedColumns={DEFAULT_COLUMNS_FULL}
                exporting={exporting}
            />
        </>
    );
}

export default QuickViewStats;